import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import {
  CONTENT_ASSET_DERIVED_FIELD_NAMES,
  CONTENT_MEDIA_FIELD_NAMES,
  isUserMediaContent,
} from '@mondaygpt/types'
import { debounce } from 'lodash'
import { apiCall } from 'modules/core/services/api'
import type { CreateCreativeDto } from 'openapi/models/CreateCreativeDto'
import type { CreativeEntity } from 'openapi/models/CreativeEntity'
import { OpenAPI } from 'openapi/core/OpenAPI'
import { CreativeService } from 'openapi/services/CreativeService'
import { analytics } from 'services/mixpanel/analytics-config'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { create } from 'zustand'
import {
  Creative,
  CreativeContentMap,
  CreativeStatus,
  CreativeType,
  CREATOR_MODE_MAP,
  defaultContent,
} from '../types'
import { useChatStatusStore } from './chat-status-store'
import { useIdeaStore } from './idea-store'
import { useWeeklyQueueStore } from '../../autopilot/store/weekly-queue-store'

// ─── Helper ─────────────────────────────────────────────────────────────────

type EntityType = CreativeEntity['type']
type CreateEntityType = CreateCreativeDto['type']

function creativeTypeToEntityType(type: CreativeType): CreateEntityType {
  return (CREATOR_MODE_MAP[type] || 'instagram') as CreateEntityType
}

export function entityToCreative(entity: CreativeEntity): Creative {
  const data = entity.data || {}
  const creativeType = (data.creativeType as CreativeType) || entityTypeToCreativeType(entity.type)

  const content = data.content || { ...defaultContent[creativeType] }

  // Server stamps `data.publishFailure` on permanent publish failure. Trust
  // shape only if `reason` is a string — legacy rows / partial writes should
  // fall through to `undefined` rather than leak an `[object Object]` into
  // the failure chip. Guard `error` and `failedAt` the same way instead of
  // `String(x ?? '')`: `String({})` stringifies to "[object Object]", which
  // would then flow into any surface that shows the raw error.
  const rawFailure = (data as { publishFailure?: unknown }).publishFailure as
    { reason?: unknown; error?: unknown; failedAt?: unknown } | undefined
  const publishFailure =
    rawFailure && typeof rawFailure.reason === 'string'
      ? {
          error: typeof rawFailure.error === 'string' ? rawFailure.error : '',
          reason: rawFailure.reason,
          failedAt: typeof rawFailure.failedAt === 'string' ? rawFailure.failedAt : '',
        }
      : undefined

  return {
    id: entity.id,
    type: creativeType,
    name: entity.name,
    status:
      data.status === CreativeStatus.Published ? CreativeStatus.Published : CreativeStatus.Draft,
    content,
    tags: data.tags || [],
    thumbnailUrl: data.thumbnailUrl || null,
    isSuggestion: !!data.isSuggestion,
    suggestionStatus:
      data.suggestionStatus === 'generating' ||
      data.suggestionStatus === 'ready' ||
      data.suggestionStatus === 'failed'
        ? data.suggestionStatus
        : undefined,
    scheduledAt: (entity.scheduledAt as unknown as string) ?? null,
    publishedAt: (entity.publishedAt as unknown as string) ?? null,
    autopilotId: entity.autopilotId ?? null,
    projectId: entity.projectId ?? null,
    pinnedAt: typeof data.pinnedAt === 'string' ? data.pinnedAt : null,
    derivedFromRevision:
      typeof data.derivedFromRevision === 'number' ? data.derivedFromRevision : null,
    publishFailure,
    createdAt: entity.createdAt,
    updatedAt: entity.updatedAt,
  }
}

const ENTITY_TO_CREATIVE_TYPE = Object.fromEntries(
  Object.entries(CREATOR_MODE_MAP).map(([k, v]) => [v, k])
) as Record<string, CreativeType>

function entityTypeToCreativeType(entityType: EntityType): CreativeType {
  return ENTITY_TO_CREATIVE_TYPE[entityType] ?? CreativeType.InstagramPost
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const now = () => new Date().toISOString()

function autoName(type: CreativeType, prompt?: string): string {
  if (prompt && prompt.length > 0) {
    const trimmed = prompt.length > 40 ? prompt.slice(0, 40) + '...' : prompt
    return trimmed
  }
  const names: Record<CreativeType, string> = {
    [CreativeType.InstagramPost]: 'Untitled Instagram Post',
    [CreativeType.FacebookPost]: 'Untitled Facebook Post',
    [CreativeType.TikTokVideo]: 'Untitled TikTok Video',
    [CreativeType.LinkedInPost]: 'Untitled LinkedIn Post',
    [CreativeType.YouTubeShort]: 'Untitled YouTube Short',
    [CreativeType.PinterestPin]: 'Untitled Pinterest Pin',
    [CreativeType.XPost]: 'Untitled X Post',
  }
  return names[type]
}

function buildCreativeData(creative: Creative): Record<string, any> {
  // Strip the derived *Url + ratio fields before persisting — they expire / are
  // re-resolved from the attachmentId on every read (CreativeAssetResolver). Driven
  // by the SAME registry as the server re-sign (CONTENT_ASSET_DERIVED_FIELD_NAMES in
  // @mondaygpt/types) so the two can't drift.
  const content = { ...(creative.content as Record<string, any>) }
  for (const field of CONTENT_ASSET_DERIVED_FIELD_NAMES) delete content[field]
  // An upload post's media changes only through PUT /projects/:id/user-media, so a
  // stale copy here can never revert it (the server rejects media changes on it).
  if (isUserMediaContent(content)) {
    for (const field of CONTENT_MEDIA_FIELD_NAMES) delete content[field]
  }

  return {
    creativeType: creative.type,
    status: creative.status,
    content,
    tags: creative.tags,
    thumbnailUrl: creative.thumbnailUrl,
  }
}

/**
 * Does applying `patch` over `content` change anything? Structural compare per
 * key (JSON), so an identical caption or an identical mediaItems array from a
 * Pusher echo reads as unchanged. Key-order drift in nested objects can only
 * produce a false "changed", never a false "same".
 */
export function contentPatchChanges(
  content: object | undefined,
  patch: Record<string, unknown>
): boolean {
  // The per-platform content types carry no index signature; read them as a bag.
  const current = (content ?? {}) as Record<string, unknown>
  return Object.entries(patch).some(
    ([key, value]) => JSON.stringify(current[key]) !== JSON.stringify(value)
  )
}

// ─── Debounced Persistence ───────────────────────────────────────────────────

const PERSIST_DEBOUNCE_MS = 500

/** Per-creative debounced persist functions, keyed by creative ID */
const debouncedPersistMap = new Map<string, ReturnType<typeof debounce>>()

/**
 * Creatives whose pending persist includes a USER content edit (typing in an
 * inline editor, image swap) as opposed to server-state mirroring (pusher
 * patches, publish metadata, AI tool results). The PUT carries
 * `userEditedContent: true` only for these — the server's multi-platform
 * propagation layer keys pin/re-derive decisions on that flag, and mirroring
 * server pushes back as "user edits" was self-pinning every fresh variant
 * within seconds of spawning.
 */
const userEditedContentIds = new Set<string>()

/** Cancel pending debounced PUT for a creative (e.g. calendar deletes before composer persist fires). */
export function cancelCreativePersist(id: string) {
  const fn = debouncedPersistMap.get(id)
  if (fn) {
    fn.cancel()
    debouncedPersistMap.delete(id)
  }
}

/**
 * Synchronously fire any pending debounced PUT for a creative through the
 * normal apiCall path. No-op if nothing is pending. Use this after discrete
 * actions (image swap) where waiting 500ms gains nothing — the user is one
 * refresh away from losing the edit otherwise.
 */
export function flushPersistNow(id: string) {
  debouncedPersistMap.get(id)?.flush()
}

/**
 * Flush pending debounced PUTs for the given creatives and WAIT for them to
 * land. confirmIdea calls this before the confirm round-trip: folding the
 * confirm response over the stores while an optimistic edit's PUT is still
 * pending would clobber the edit locally AND then persist the clobbered
 * (pre-edit) content as a user edit.
 */
export async function flushPersistsAndWait(ids: string[]): Promise<void> {
  await Promise.all(ids.map(id => debouncedPersistMap.get(id)?.flush()))
}

/**
 * Flush every pending debounced PUT via `fetch(..., { keepalive: true })` so
 * the request survives the page tear-down. Called from `pagehide` /
 * `visibilitychange:hidden` listeners. The normal apiCall path uses XHR
 * inside the OpenAPI client, which the browser aborts on navigation; only
 * `keepalive` fetch is allowed to outlive the document.
 */
export function flushAllPendingPersists() {
  if (debouncedPersistMap.size === 0) return
  const { creatives } = useCreativeStore.getState()
  const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : undefined
  let flushedCount = 0
  let failedCount = 0
  let payloadBytes = 0
  let firstCreativeId: string | undefined
  for (const [id, fn] of debouncedPersistMap) {
    fn.cancel()
    const creative = creatives.find(c => c.id === id)
    if (!creative) continue
    if (!firstCreativeId) firstCreativeId = id
    const body = JSON.stringify({
      name: creative.name,
      data: buildCreativeData(creative),
      ...(userEditedContentIds.delete(id) ? { userEditedContent: true } : {}),
    })
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    try {
      // Fire-and-forget: the page is being torn down. `keepalive: true` lets
      // the body finish flushing after the document is gone (64 KB cap, well
      // above any creative payload).
      void fetch(`${OpenAPI.BASE}/v1/creatives/${id}`, {
        method: 'PUT',
        headers,
        body,
        credentials: 'include',
        keepalive: true,
      })
      flushedCount += 1
      payloadBytes += body.length
    } catch (err) {
      // keepalive fetch can throw synchronously (e.g. body > 64KB). Capture
      // so we can see how often this silently drops an edit in the wild.
      failedCount += 1
      errorReportingService.captureException(err as Error, {
        feature: 'composer_teardown_flush',
        creativeId: id,
        payloadBytes: body.length,
      })
    }
  }
  if (flushedCount > 0 || failedCount > 0) {
    analytics.track(AnalyticsEvents.Composer.COMPOSER_EDIT_FLUSHED_ON_TEARDOWN, {
      flushed_count: flushedCount,
      failed_count: failedCount,
      payload_bytes: payloadBytes,
      creative_id: firstCreativeId,
    })
  }
  debouncedPersistMap.clear()
}

// ─── Store ──────────────────────────────────────────────────────────────────

interface CreativeStore {
  creatives: Creative[]
  loading: boolean
  loaded: boolean

  /** Per-creative map of content fields currently being generated (e.g. 'image', 'caption', 'hashtags') */
  generatingFields: Map<string, Set<string>>

  /**
   * Per-creative count of SERVER content applies (`applyServerContentPatch`:
   * Pusher patches, AI tool results, truncated-event recovery). The canvas
   * providers key their post-revise refetch on it so a caption-only AI revise,
   * which moves no slide attachment, still re-syncs the deck. User typing
   * (`updateCreativeContent`) never bumps it: a refetch there would reset the
   * active slide editor on every keystroke.
   */
  serverContentVersions: Record<string, number>

  // Async
  fetchCreatives: () => Promise<void>
  createCreative: (
    type: CreativeType,
    prompt?: string,
    initialContent?: CreativeContentMap[CreativeType]
  ) => Promise<Creative>
  deleteCreative: (id: string) => Promise<void>

  // Local mutations (optimistic, debounced persist)
  updateCreative: (id: string, updates: Partial<Omit<Creative, 'id' | 'type'>>) => void
  /**
   * Optimistic content edit + debounced persist. Defaults to a USER edit
   * (the PUT carries `userEditedContent: true`, which drives server-side
   * variant pin/re-derive). Pass `{ userEdit: false }` when mirroring
   * server-known state (publish metadata, AI tool results) so the persist
   * doesn't masquerade as a human edit.
   */
  updateCreativeContent: (
    id: string,
    contentUpdates: Record<string, unknown>,
    opts?: { userEdit?: boolean }
  ) => void
  /**
   * Apply server-known content (Pusher `creative:content:updated`, an AI tool
   * result, a refetch after a truncated event) to local state WITHOUT
   * scheduling a persist. Echoing pushes back as PUTs created write loops:
   * stale mid-revise overwrites, spurious "user edit" signals, and revision
   * inflation. Also mirrors into the idea store (the composer's controlled
   * inputs read the active creative from there) and bumps
   * `serverContentVersions[id]` when something actually changed. An empty or
   * undefined patch (a truncated event) is a no-op.
   */
  applyServerContentPatch: (id: string, contentUpdates: Record<string, unknown> | undefined) => void
  /**
   * Apply server-known NON-content fields (e.g. the name a `renameCreative`
   * tool already persisted) to local state without scheduling a persist.
   * `updateCreative` would echo the whole creative, stale content included.
   */
  applyServerCreativePatch: (id: string, updates: Partial<Omit<Creative, 'id' | 'type'>>) => void

  /** Set the full set of generating fields for a creative */
  setGeneratingFields: (id: string, fields: Set<string>) => void
  /** Add field(s) to the generating set for a creative */
  addGeneratingFields: (id: string, fields: string[]) => void
  /** Clear all generating fields for a creative */
  clearGenerating: (id: string) => void
}

export const useCreativeStore = create<CreativeStore>((set, get) => {
  /**
   * Debounced persist for a given creative ID.
   * Batches rapid updates into a single PUT request.
   * On failure, rolls back optimistic state by refetching from server.
   */
  function persistCreative(id: string) {
    let fn = debouncedPersistMap.get(id)
    if (!fn) {
      fn = debounce(async () => {
        const creative = get().creatives.find(c => c.id === id)
        if (!creative) return

        const userEdited = userEditedContentIds.delete(id)
        try {
          await apiCall(CreativeService.creativeControllerUpdateV1, null, id, {
            name: creative.name,
            data: buildCreativeData(creative),
            ...(userEdited ? { userEditedContent: true } : {}),
          })
        } catch (err) {
          console.error('Failed to persist creative, rolling back:', err)
          // Rollback: refetch server state
          try {
            const { creative: entity } = await apiCall(
              CreativeService.creativeControllerFindOneV1,
              null,
              id
            )
            set(state => ({
              creatives: state.creatives.map(c => (c.id === id ? entityToCreative(entity) : c)),
            }))
          } catch {
            // Best effort rollback
          }
        }
      }, PERSIST_DEBOUNCE_MS)
      debouncedPersistMap.set(id, fn)
    }
    fn()
  }

  return {
    creatives: [],
    loading: false,
    loaded: false,
    generatingFields: new Map<string, Set<string>>(),
    serverContentVersions: {},

    fetchCreatives: async () => {
      if (get().loading) return
      set({ loading: true })
      try {
        const entities = await apiCall(CreativeService.creativeControllerFindAllV1, null)
        set({ creatives: entities.map(entityToCreative), loaded: true })
      } catch (err) {
        console.error('Failed to fetch creatives:', err)
      } finally {
        set({ loading: false })
      }
    },

    createCreative: async (
      type: CreativeType,
      prompt?: string,
      initialContent?: CreativeContentMap[CreativeType]
    ) => {
      const name = autoName(type, prompt)
      const content = initialContent ? { ...initialContent } : { ...defaultContent[type] }
      const entityType = creativeTypeToEntityType(type)

      const { creative: entity } = await apiCall(CreativeService.creativeControllerCreateV1, null, {
        name,
        type: entityType,
        data: {
          creativeType: type,
          status: CreativeStatus.Draft,
          content,
          tags: [],
          thumbnailUrl: null,
        },
      })

      const creative = entityToCreative(entity)
      set(state => ({ creatives: [creative, ...state.creatives] }))
      return creative
    },

    updateCreative: (id: string, updates: Partial<Omit<Creative, 'id' | 'type'>>) => {
      set(state => ({
        creatives: state.creatives.map(a =>
          a.id === id ? { ...a, ...updates, updatedAt: now() } : a
        ),
      }))
      persistCreative(id)
    },

    updateCreativeContent: (
      id: string,
      contentUpdates: Record<string, unknown>,
      opts?: { userEdit?: boolean }
    ) => {
      set(state => ({
        creatives: state.creatives.map(a => {
          if (a.id !== id) return a
          return {
            ...a,
            content: { ...a.content, ...contentUpdates },
            updatedAt: now(),
          }
        }),
      }))
      // Mirror into idea-store so Post-mode reads (idea.primaryCreative /
      // siblingsByProject) reflect local edits. Without this, the composer's
      // controlled inputs would snap back on every keystroke because
      // ComposerPage reads activeCreative from idea-store.
      useIdeaStore.getState().patchCreativeContent(id, contentUpdates)
      if (opts?.userEdit !== false) {
        userEditedContentIds.add(id)
        // Mirror the server's pin/revision semantics optimistically so the
        // composer's Customized / behind-the-post treatments appear without
        // waiting for a refetch (the server is the source of truth on load).
        const creative = get().creatives.find(c => c.id === id)
        if (creative?.projectId) {
          const ideaStore = useIdeaStore.getState()
          const idea = ideaStore.ideas.find(i => i.projectId === creative.projectId)
          if (idea) {
            if (idea.primaryCreative?.id === id) {
              ideaStore.noteCanonicalEditLocally(creative.projectId)
            } else {
              ideaStore.markSiblingPinnedLocally(creative.projectId, id)
              if (!creative.pinnedAt) {
                set(state => ({
                  creatives: state.creatives.map(c =>
                    c.id === id ? { ...c, pinnedAt: new Date().toISOString() } : c
                  ),
                }))
              }
            }
          }
        }
      }
      persistCreative(id)
    },

    applyServerContentPatch: (id: string, contentUpdates: Record<string, unknown> | undefined) => {
      if (!contentUpdates || Object.keys(contentUpdates).length === 0) return
      // Mirror first, unconditionally: the idea store may hold this creative
      // (as a primary or a platform sibling) even when `creatives` does not.
      useIdeaStore.getState().patchCreativeContent(id, contentUpdates)
      set(state => {
        const target = state.creatives.find(c => c.id === id)
        // A patch that changes nothing (the Pusher echo of a tool result the
        // client already applied) must not re-render or bump the version, or
        // the deck editor would refetch and reset for no reason.
        if (target && !contentPatchChanges(target.content, contentUpdates)) return state
        return {
          creatives: state.creatives.map(a => {
            if (a.id !== id) return a
            return {
              ...a,
              content: { ...a.content, ...contentUpdates },
              updatedAt: now(),
            }
          }),
          serverContentVersions: {
            ...state.serverContentVersions,
            [id]: (state.serverContentVersions[id] ?? 0) + 1,
          },
        }
      })
    },

    applyServerCreativePatch: (id: string, updates: Partial<Omit<Creative, 'id' | 'type'>>) => {
      set(state => ({
        creatives: state.creatives.map(a =>
          a.id === id ? { ...a, ...updates, updatedAt: now() } : a
        ),
      }))
    },

    deleteCreative: async (id: string) => {
      // Deleting a creative deletes its whole Post: the server routes
      // DELETE /v1/creatives/{id} through the parent Project delete, so the FK
      // cascade takes every platform sibling with it. Mirror that locally —
      // strip ALL creatives sharing this one's projectId (not just the clicked
      // id) plus the matching idea, so the optimistic UI doesn't briefly show
      // siblings the server just cascaded away. Snapshot everything for rollback.
      const target = get().creatives.find(c => c.id === id)
      const projectId = target?.projectId ?? null
      const prevCreatives = projectId
        ? get().creatives.filter(c => c.projectId === projectId)
        : target
          ? [target]
          : []
      const removedIds = new Set(prevCreatives.map(c => c.id))
      // Fall back to the clicked id if it somehow wasn't in the store.
      if (removedIds.size === 0) removedIds.add(id)

      for (const removedId of removedIds) {
        cancelCreativePersist(removedId)
        useChatStatusStore.getState().clearCreative(removedId)
      }

      const prevIdea = projectId
        ? useIdeaStore.getState().ideas.find(i => i.projectId === projectId)
        : undefined

      set(state => ({ creatives: state.creatives.filter(c => !removedIds.has(c.id)) }))
      if (projectId) {
        useIdeaStore.setState(state => ({
          ideas: state.ideas.filter(i => i.projectId !== projectId),
        }))
      }

      try {
        await apiCall(CreativeService.creativeControllerDeleteV1, null, id)
        // Drop the post from the cockpit immediately (its own Zustand store);
        // without this it lingers until the next manual fetch. Mirrors deletePost.
        void useWeeklyQueueStore.getState().fetchQueue()
      } catch (err) {
        // Restore every optimistic removal so the UI matches server state.
        if (prevCreatives.length > 0) {
          set(state => {
            const existing = new Map(state.creatives.map(c => [c.id, c]))
            for (const c of prevCreatives) if (!existing.has(c.id)) existing.set(c.id, c)
            return { creatives: Array.from(existing.values()) }
          })
        }
        if (projectId && prevIdea) {
          useIdeaStore.setState(state => ({
            ideas: state.ideas.some(i => i.projectId === projectId)
              ? state.ideas
              : [...state.ideas, prevIdea],
          }))
        }
        console.error('Failed to delete creative:', err)
        throw err
      }
    },

    setGeneratingFields: (id: string, fields: Set<string>) => {
      set(state => {
        const next = new Map(state.generatingFields)
        next.set(id, fields)
        return { generatingFields: next }
      })
    },

    addGeneratingFields: (id: string, fields: string[]) => {
      set(state => {
        const next = new Map(state.generatingFields)
        const existing = next.get(id) || new Set<string>()
        const merged = new Set(existing)
        for (const f of fields) merged.add(f)
        next.set(id, merged)
        return { generatingFields: next }
      })
    },

    clearGenerating: (id: string) => {
      set(state => {
        const next = new Map(state.generatingFields)
        next.delete(id)
        return { generatingFields: next }
      })
    },
  }
})
